import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DriverService } from '../../../core/services/driver.service';
import { PassengerService } from '../../../core/services/passenger.service';
import { InterestedDriver } from '../../../core/models/driver.model';
import { InterestedPassenger } from '../../../core/models/passenger.model';
import { RouterLink, RouterLinkActive } from '@angular/router';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit {
  // Reactive form
  unifiedForm!: FormGroup;

  // Driver form data
  driverData: InterestedDriver = {
    name: '',
    email: '',
    phone: '',
    userType: '',
    origin: '',
    destination: '',
    interestType: ''
  };

  // Passenger form data
  passengerData: InterestedPassenger = {
    name: '',
    email: '',
    phone: '',
    userType: '',
    interestType: '',
    originCity: '',
    destinationCity: ''
  };

  isSubmitting = false;
  isSubmittingDriver = false;
  isSubmittingPassenger = false;
  submissionMessage = '';
  driverSubmissionMessage = '';
  passengerSubmissionMessage = '';
  
  // CAPTCHA
  captchaText = '';
  captchaValid = false;

  constructor(
    private formBuilder: FormBuilder,
    private driverService: DriverService,
    private passengerService: PassengerService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.generateCaptcha();
  }

  private initializeForm() {
    this.unifiedForm = this.formBuilder.group({
      userType: ['', Validators.required],
      fullName: ['', [
        Validators.required, 
        Validators.minLength(2), 
        Validators.maxLength(50),
        Validators.pattern('^[a-zA-ZáéíóúÁÉÍÓÚñÑ\\s]+$')
      ]],
      email: ['', [
        Validators.required, 
        Validators.email,
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
      ]],
      phone: ['', [
        Validators.required,
        Validators.pattern('^[0-9]{7,15}$')
      ]],
      tripPurpose: ['', Validators.required],
      origin: ['', [Validators.required, Validators.maxLength(100)]],
      destination: ['', [Validators.required, Validators.maxLength(100)]],
      captcha: ['', Validators.required],
      dataConsent: [false, Validators.requiredTrue]
    });
  }

  scrollToSubscribe(event: Event) {
    setTimeout(() => {
      const el = document.getElementById('footer-subscribe-form');
      if (el) {
        // Get the header height - adjusted to show section titles properly
        const headerHeight = 100; // Fixed header height + padding for title visibility
        const elementPosition = el.offsetTop - headerHeight;
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }, 300);
  }

  async onDriverFormSubmit(event: Event) {
    event.preventDefault();
    
    if (!this.validateDriverForm()) {
      return;
    }

    this.isSubmittingDriver = true;
    this.driverSubmissionMessage = '';

    try {
      const driverId = await this.driverService.saveInterestedDriver(this.driverData);
      
      this.driverSubmissionMessage = '¡Información enviada correctamente! Te contactaremos pronto.';
      this.resetDriverForm();
      
      console.log('Driver saved successfully with ID:', driverId);
      
    } catch (error) {
      console.error('Error saving driver data:', error);
      this.driverSubmissionMessage = 'Error al enviar la información. Por favor, intenta nuevamente.';
    } finally {
      this.isSubmittingDriver = false;
    }
  }

  async onPassengerFormSubmit(event: Event) {
    event.preventDefault();
    
    if (!this.validatePassengerForm()) {
      return;
    }

    this.isSubmittingPassenger = true;
    this.passengerSubmissionMessage = '';

    try {
      const passengerId = await this.passengerService.saveInterestedPassenger(this.passengerData);
      
      this.passengerSubmissionMessage = '¡Información enviada correctamente! Te contactaremos pronto.';
      this.resetPassengerForm();
      
      console.log('Passenger saved successfully with ID:', passengerId);
      
    } catch (error) {
      console.error('Error saving passenger data:', error);
      this.passengerSubmissionMessage = 'Error al enviar la información. Por favor, intenta nuevamente.';
    } finally {
      this.isSubmittingPassenger = false;
    }
  }

  private validateDriverForm(): boolean {
    if (!this.driverData.name || !this.driverData.email || !this.driverData.phone || 
        !this.driverData.interestType) {
      this.driverSubmissionMessage = 'Por favor, completa todos los campos.';
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.driverData.email)) {
      this.driverSubmissionMessage = 'Por favor, ingresa un correo electrónico válido.';
      return false;
    }

    return true;
  }

  private validatePassengerForm(): boolean {
    if (!this.passengerData.name || !this.passengerData.email || !this.passengerData.phone || 
        !this.passengerData.originCity || !this.passengerData.destinationCity) {
      this.passengerSubmissionMessage = 'Por favor, completa todos los campos.';
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.passengerData.email)) {
      this.passengerSubmissionMessage = 'Por favor, ingresa un correo electrónico válido.';
      return false;
    }

    return true;
  }

  private resetDriverForm() {
    this.driverData = {
      name: '',
      email: '',
      phone: '',
      userType: '',
      origin: '',
      destination: '',
      interestType: ''
    };
  }

  private resetPassengerForm() {
    this.passengerData = {
      name: '',
      email: '',
      phone: '',
      userType: '',
      interestType: '',
      originCity: '',
      destinationCity: ''
    };
  }

  // Unified form handling
  async onFormSubmit(event: Event) {
    event.preventDefault();
    
    if (!this.validateUnifiedForm()) {
      return;
    }

    this.isSubmitting = true;
    this.submissionMessage = '';
    const formValue = this.unifiedForm.value;

    try {
      if (formValue.userType === 'conductor') {
        // Map form data to driver data
        this.driverData = {
          name: formValue.fullName,
          email: formValue.email,
          phone: formValue.phone,
          userType: formValue.userType,
          origin: formValue.origin,
          destination: formValue.destination,
          interestType: formValue.tripPurpose
        };
        
        const driverId = await this.driverService.saveInterestedDriver(this.driverData);

        Swal.fire({
          title: '¡Éxito!',
          text: 'Registro exitoso próximamente harás parte de la comunidad Routigo',
          icon: 'success',
          confirmButtonText: 'Aceptar'
        });

        console.log('Driver saved successfully with ID:', driverId);
      } else if (formValue.userType === 'pasajero') {
        // Map form data to passenger data
        this.passengerData = {
          name: formValue.fullName,
          email: formValue.email,
          phone: formValue.phone,
          userType: formValue.userType,
          interestType: formValue.tripPurpose,
          originCity: formValue.origin,
          destinationCity: formValue.destination
        };
        
        const passengerId = await this.passengerService.saveInterestedPassenger(this.passengerData);
        
         Swal.fire({
          title: '¡Éxito!',
          text: 'Registro exitoso próximamente harás parte de la comunidad Routigo',
          icon: 'success',
          confirmButtonText: 'Aceptar'
        });
        
        console.log('Passenger saved successfully with ID:', passengerId);
      }
      
      this.submissionMessage = '¡Información enviada correctamente! Te contactaremos pronto.';
      this.resetUnifiedForm();
      
    } catch (error) {
      console.error('Error saving data:', error);
      this.submissionMessage = 'Error al enviar la información. Por favor, intenta nuevamente.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private validateUnifiedForm(): boolean {
    const formValue = this.unifiedForm.value;
    
    // Check if form is valid
    if (this.unifiedForm.invalid) {
      this.submissionMessage = 'Por favor, completa todos los campos obligatorios correctamente.';
      this.unifiedForm.markAllAsTouched();
      return false;
    }

    // Validate CAPTCHA
    if (formValue.captcha !== this.captchaText) {
      this.submissionMessage = 'El código de verificación es incorrecto.';
      return false;
    }

    return true;
  }

  private resetUnifiedForm() {
    this.unifiedForm.reset({
      userType: '',
      fullName: '',
      email: '',
      phone: '',
      tripPurpose: '',
      origin: '',
      destination: '',
      captcha: '',
      dataConsent: false
    });
    this.generateCaptcha();
  }

  // Helper methods for form field access
  get userType() { return this.unifiedForm.get('userType'); }
  get fullName() { return this.unifiedForm.get('fullName'); }
  get email() { return this.unifiedForm.get('email'); }
  get phone() { return this.unifiedForm.get('phone'); }
  get tripPurpose() { return this.unifiedForm.get('tripPurpose'); }
  get origin() { return this.unifiedForm.get('origin'); }
  get destination() { return this.unifiedForm.get('destination'); }
  get captcha() { return this.unifiedForm.get('captcha'); }
  get dataConsent() { return this.unifiedForm.get('dataConsent'); }

  // CAPTCHA methods
  generateCaptcha() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.captchaText = result;
    this.captchaValid = false;
  }

  // Modal methods
  showDataPolicyModal() {
    // Get the modal element using vanilla JavaScript
    const modalElement = document.getElementById('dataPolicyModal');
    if (modalElement) {
      // Use Bootstrap 5 modal API
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }
}
